import axios, { AxiosInstance } from 'axios';

// 使用相对路径，通过 Nginx 代理访问 admin API
const ADMIN_API_URL = '/api/admin';

class AdminApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: ADMIN_API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // 请求拦截器：添加 token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('admin_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // 响应拦截器：处理 401 错误
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('admin_token');
          window.location.href = '/admin/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // 登录
  async login(username: string, password: string) {
    const response = await this.api.post('/login', {
      username,
      password,
    });
    if (response.data.token) {
      localStorage.setItem('admin_token', response.data.token);
    }
    return response.data;
  }

  // 验证 token
  async verify() {
    const response = await this.api.get('/verify');
    return response.data;
  }

  // 登出
  async logout() {
    try {
      await this.api.post('/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('admin_token');
      window.location.href = '/admin/login';
    }
  }

  // 检查是否已登录
  isAuthenticated(): boolean {
    return !!localStorage.getItem('admin_token');
  }

  // 获取数据
  async getData(type: 'banners' | 'products' | 'articles' | 'materials') {
    const response = await this.api.get(`/data/${type}`);
    return response.data;
  }

  // 更新数据
  async updateData(
    type: 'banners' | 'products' | 'articles' | 'materials',
    data: any
  ) {
    const response = await this.api.put(`/data/${type}`, { data });
    return response.data;
  }

  // 上传文件
  async uploadFile(
    file: File,
    uploadType: 'images' | 'pdf',
    subDir?: string,
    fileName?: string
  ) {
    const formData = new FormData();
    formData.append('file', file);

    // 构建查询参数
    const params = new URLSearchParams();
    params.append('uploadType', uploadType);
    if (subDir) {
      params.append('subDir', subDir);
    }
    if (fileName) {
      params.append('fileName', fileName);
    }

    const response = await this.api.post(`/upload?${params.toString()}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // 获取文件列表
  async getFiles(type: 'images' | 'pdf', subDir?: string) {
    const params = subDir ? { subDir } : {};
    const response = await this.api.get(`/files/${type}`, { params });
    return response.data;
  }

  // 删除文件
  async deleteFile(filePath: string) {
    const response = await this.api.delete('/files', {
      data: { filePath },
    });
    return response.data;
  }
}

export const adminApiService = new AdminApiService();
export default adminApiService;
